import { DurationPickerV2 as DurationPicker } from '@automata/ui';
import Stack from '@mui/material/Stack';
import lowerCase from 'lodash/lowerCase';

import { InstrumentSelect } from './InstrumentSelect';
import { TaskMenu } from './TaskMenu';
import type { InstrumentForLocation, TaskPropertiesForm } from '../../../types';
import Chip from '../../ChipWFB';
import { Icon } from '../../customIcons/tasks';
import { TextFieldWithMaxLength } from '../../TextFieldWithMaxLength';

type Props = {
  handlePendingChange: (update: Partial<TaskPropertiesForm>) => void;
  handleSubmitForm: (override?: Partial<TaskPropertiesForm>) => void;
  instruments: InstrumentForLocation[];
  showTimeEstimateField: boolean;
  values: TaskPropertiesForm;
};

export const BaseTaskSettings = ({
  handlePendingChange,
  handleSubmitForm,
  instruments,
  showTimeEstimateField,
  values,
}: Props): JSX.Element => {
  const {
    id,
    instrumentId,
    nickname,
    taskType,
    userTimeEstimateHours,
    userTimeEstimateMinutes,
    userTimeEstimateSeconds,
  } = values;

  return (
    <Stack
      gap={1}
      onBlur={() => {
        handleSubmitForm();
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        pb={1}
      >
        {/* minHeight is a quick way keep visual consistency between this component and CodeTaskFields */}
        <Stack direction="row" alignItems="center" gap={2} minHeight={50}>
          <Icon color="neutral" fontSize="large" iconType={taskType} />
          <Chip label={lowerCase(taskType)} />
        </Stack>
        <TaskMenu taskId={id} />
      </Stack>

      <TextFieldWithMaxLength
        label="Task name"
        fullWidth
        value={nickname}
        onChange={(e) => handlePendingChange({ nickname: e.target.value })}
        maxLength={50}
      />

      <InstrumentSelect
        label="Instrument"
        value={instrumentId}
        instruments={instruments}
        onChange={(value) => {
          handlePendingChange({ instrumentId: value ?? undefined });
        }}
      />

      {showTimeEstimateField && (
        <>
          <DurationPicker
            label="Estimated time"
            hours={userTimeEstimateHours}
            minutes={userTimeEstimateMinutes}
            seconds={userTimeEstimateSeconds}
            handleUpdateTime={(timePatch) => {
              let formPatch: Partial<TaskPropertiesForm> = {};
              if ('hours' in timePatch) {
                formPatch['userTimeEstimateHours'] = timePatch.hours;
              }
              if ('minutes' in timePatch) {
                formPatch['userTimeEstimateMinutes'] = timePatch.minutes;
              }
              if ('seconds' in timePatch) {
                formPatch['userTimeEstimateSeconds'] = timePatch.seconds;
              }
              handlePendingChange(formPatch);
            }}
          />
        </>
      )}
    </Stack>
  );
};
